import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { mapBuildDto, mapJobListItemDto, mapQueryResult } from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import { mergeProjectFilters } from '@/shared/data/queryFilters';
import type { GridFilters } from '@/ui/composites/grid/gridFilters';
import type { BuildDto, JobListItemDto, QueryResult, QuerySort } from '@flowqa/shared';

export type JobFilterCountsDto = {
  pipelines: Record<string, number>;
  stages: Record<string, number>;
  jobs: Record<string, number>;
  tags: Record<string, number>;
};

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (): string => {
  const token = getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

export const queryJobs = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: GridFilters;
    search?: string;
  },
): Promise<QueryResult<JobListItemDto>> => {
  const token = ensureToken();
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  try {
    const result = await apiClient.post<QueryResult<JobListItemDto>>(
      'jobs/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts: input?.sorts ?? [{ field: 'jobName', direction: 'asc' }],
        filters: combinedFilters,
        search: input?.search,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    return mapQueryResult(result, mapJobListItemDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryJobRuns = async (
  projectId?: string | null,
  organisationId?: string | null,
  input?: {
    page: number;
    pageSize: number;
    sorts: QuerySort[];
    filters: GridFilters;
    search?: string;
  },
): Promise<QueryResult<BuildDto>> => {
  const token = ensureToken();
  const filters: GridFilters = input?.filters ?? [];
  const combinedFilters = mergeProjectFilters(filters, projectId);
  try {
    const result = await apiClient.post<QueryResult<BuildDto>>(
      'job-runs/query',
      {
        page: input?.page ?? 1,
        pageSize: input?.pageSize ?? 50,
        sorts: input?.sorts ?? [{ field: 'startedAt', direction: 'desc' }],
        filters: combinedFilters,
        search: input?.search,
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
    return mapQueryResult(result, mapBuildDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryJobFilterCounts = async (
  projectId: string | null,
  organisationId: string | null | undefined,
  input: {
    search?: string;
    filters: {
      pipelines: GridFilters;
      stages: GridFilters;
      jobs: GridFilters;
      tags: GridFilters;
    };
  },
): Promise<JobFilterCountsDto> => {
  const token = ensureToken();
  const withProjectFilters = (filters: GridFilters): GridFilters =>
    mergeProjectFilters(filters, projectId);
  try {
    return await apiClient.post<JobFilterCountsDto>(
      'jobs/filter-counts',
      {
        search: input.search,
        filters: {
          pipelines: withProjectFilters(input.filters.pipelines),
          stages: withProjectFilters(input.filters.stages),
          jobs: withProjectFilters(input.filters.jobs),
          tags: withProjectFilters(input.filters.tags),
        },
      },
      { retries: 1, orgId: organisationId ?? undefined, token },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const getJobById = async (
  id: string,
  organisationId?: string | null,
): Promise<JobListItemDto> => {
  const token = ensureToken();
  try {
    const result = await apiClient.get<JobListItemDto>(`jobs/${id}`, {
      retries: 1,
      orgId: organisationId ?? undefined,
      token,
    });
    return mapJobListItemDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};
